function _defineProperty(e, r, t) { return (r = _toPropertyKey(r)) in e ? Object.defineProperty(e, r, { value: t, enumerable: !0, configurable: !0, writable: !0 }) : e[r] = t, e; }
function _toPropertyKey(t) { var i = _toPrimitive(t, "string"); return "symbol" == typeof i ? i : i + ""; }
function _toPrimitive(t, r) { if ("object" != typeof t || !t) return t; var e = t[Symbol.toPrimitive]; if (void 0 !== e) { var i = e.call(t, r || "default"); if ("object" != typeof i) return i; throw new TypeError("@@toPrimitive must return a primitive value."); } return ("string" === r ? String : Number)(t); }
import React from "react";
import { withRouter } from "react-router";
import { Container, FlexboxContainer, CssContainer } from "../../components/container";
import { PageHeader, Ribbon, Card, Pill, Empty, Search, Filters, Delta, Pending } from "../../components/analytics-kit";

// territory-common deltas ({text, dir, pct}) in the kit's shape; null stays null ("no comparison").
const kitDelta = d => d && d.pct != null ? {
  dir: d.dir,
  pct: d.pct
} : null;
import ComparisonPicker from "../../components/comparison-picker";
import StatusFilter from "../../components/status-filter";
import PlunkDropDown from "../../components/plunk-dropdown";
import { Loader, ChartLoader } from "../../components/loaders";
import Error from "../../components/error";
import Chart from "../../components/chart";
import { hasTenantFeature } from "../../functions/tenant-features";
import RangePicker from "../../components/range-picker";
import { rangeLabelFor, RANGES, num, usd, aov, fetchSales, bucketFor, trendLabel, stateName, deltaOf, deltaOfPaced, comparisonQs, hasPrevTrend, PREV_LINE_COLOR, accountLensKv, readAcctLens, writeAcctLens, readBasis, writeBasis, titleCase, isCustomRange } from "./territory-common";
import { money, placeName, placeList, isHouseRep, repName, BasisSelect, ShowAll } from "./territory-redesign-kit";
import account from "../../data/account";
import { AccountFilterControl, AccountFilterBanner } from "./account-filter";
import { ActivityComparisonMenu } from "../../components/container-activity-menus";
import compLabel from "../../functions/comp-label";
import { Link } from "react-router-dom";
import { getGlobalRange, getGlobalComp, subscribePeriod, unsubscribePeriod } from "../../functions/period-store";
import SalesMapCard from "./map-card";
import { getGeoHeatCells } from "./sales-api";

/*
 * Territory (Market) Detail — §5 of the Territory & Rep Intelligence spec.
 * Opened from a choropleth state click or a territory table row.
 *
 * Scope = one dealer ship-to state (`region`). Everything is server-scoped
 * via the 1.1.5 drill-through params:
 *   /sales/totals/{range}?region=X   → market KPI header (distinct counts)
 *   /sales/trend/{range}?region=X    → market sales trend
 *   /sales/reps/{range}?region=X     → reps by their sales INTO this market
 *   /sales/customers/{range}?region=X&top=100 → dealers in the market
 *
 * "Assigned to cover" is derived from the rep roster's `states` list
 * (sales_rep_territory); a rep can sell into a market without covering it
 * — that's the attribution insight surfaced on the overview.
 *
 * (The /territory/owner endpoint is keyed by locationGroupId, not state,
 * so market ownership here is shown via covering reps instead.)
 */

class TerritoryDetailViewComponent extends React.Component {
  constructor(props) {
    super(props);
    // Account lens (SALES_ACCOUNT_FILTER.md) — editable here too; persists per-tenant
    // (shared with the overview) and re-scopes this market's sections server-side.
    _defineProperty(this, "setAcctLens", (mode, ids) => {
      ids = [...new Set((ids || []).map(String))];
      writeAcctLens((account.get() || {}).tenantId, mode, ids);
      this.setState({
        acctMode: mode,
        acctIds: ids,
        data: null,
        error: false
      }, () => this.load());
    });
    _defineProperty(this, "removeAcct", id => this.setAcctLens(this.state.acctMode, this.state.acctIds.filter(x => String(x) !== String(id))));
    _defineProperty(this, "clearAcct", () => this.setAcctLens(this.state.acctMode, []));
    _defineProperty(this, "onRange", e => {
      this.props.history.push(`/Territory/${e.target.value}/region/${encodeURIComponent(this.region())}`);
    });
    _defineProperty(this, "onCompSelected", e => {
      const comp = e.detail.item.dataset.value;
      if (comp === "unavailable" || comp === this.state.comp) return;
      this.setState({
        comp,
        data: null,
        error: false
      }, () => this.load());
    });
    _defineProperty(this, "onBasis", v => {
      const basis = v === "ship" ? "ship" : "bill";
      if (basis === this.state.basis) return;
      writeBasis((account.get() || {}).tenantId, basis);
      this.setState({
        basis,
        data: null,
        error: false
      }, () => this.load());
    });
    _defineProperty(this, "back", () => {
      this.props.history.push(`/Territory/${this.range()}`);
    });
    _defineProperty(this, "openRep", repCode => {
      if (!repCode) return;
      this.props.history.push(`/Territory/${this.range()}/rep/${encodeURIComponent(repCode)}`);
    });
    _defineProperty(this, "openCustomer", accountNumber => {
      if (!accountNumber) return;
      this.props.history.push(`/Territory/${this.range()}/customer/${encodeURIComponent(accountNumber)}`);
    });
    const lens = readAcctLens((account.get() || {}).tenantId);
    this.state = {
      data: null,
      error: false,
      status: [],
      comp: getGlobalComp("prev_period"),
      acctMode: lens.mode,
      acctIds: lens.ids,
      // Dealer placement (redesign 2026-10-09): bill = head office (default), ship = delivery location.
      basis: readBasis((account.get() || {}).tenantId),
      gapsAll: false,
      dealersAll: false
    };
    this._alive = true;
  }
  range() {
    return this.props.params && this.props.params.range || getGlobalRange();
  }
  region() {
    return decodeURIComponent(this.props.params && this.props.params.region || "");
  }
  componentDidMount() {
    this.load();
    this._periodTok = subscribePeriod(({
      range,
      comp
    }) => {
      if (!this._alive || !range) return;
      if (comp && comp !== this.state.comp) this.setState({
        comp
      });
      const target = `/Territory/${range}/region/${encodeURIComponent(this.region())}`;
      if (window.location.pathname === target) {
        this.setState({
          data: null,
          error: false
        }, () => this.load());
      } else {
        this.props.history.push(target);
      }
    });
  }
  componentWillUnmount() {
    this._alive = false;
    unsubscribePeriod(this._periodTok);
  }
  componentDidUpdate(prev) {
    const prevKey = `${prev.params && prev.params.range || ""}|${prev.params && prev.params.region || ""}`;
    const key = `${this.props.params && this.props.params.range || ""}|${this.props.params && this.props.params.region || ""}`;
    if (prevKey !== key) {
      this.setState({
        data: null,
        error: false
      });
      this.load();
    }
  }
  async load() {
    const range = this.range();
    this.setState({
      areaGaps: undefined
    });
    getGeoHeatCells(range, "sessions").then(cells => this._alive && this.setState({
      areaGaps: cells
    })).catch(() => this._alive && this.setState({
      areaGaps: null
    }));
    const comp = this.state.comp;
    const region = encodeURIComponent(this.region());
    const bucket = bucketFor(range);
    const st = (this.state.status || []).join(",");
    const ws = u => st ? u + (u.includes("?") ? "&" : "?") + "status=" + encodeURIComponent(st) : u;
    // Account lens from state (editable via the on-page control); re-scopes the
    // market's sections server-side. The picker's own source (acctSource) is the
    // UNSCOPED roster so you can pick any account, not just this market's dealers.
    const _acctKv = accountLensKv(this.state.acctMode, this.state.acctIds);
    const wsa = u => {
      const x = ws(u);
      return _acctKv ? x + (x.includes("?") ? "&" : "?") + _acctKv : x;
    };
    // Every territory call carries the dealer-placement basis (bill | ship). The demand-gap
    // rail (getGeoHeatCells) is always by delivery location, so it never gets it.
    const basisKv = "basis=" + (this.state.basis === "ship" ? "ship" : "bill");
    const wb = u => u + (u.includes("?") ? "&" : "?") + basisKv;
    try {
      const [totals, trend, reps, customers, topProducts, acctSource, territories] = await Promise.all([fetchSales(wb(wsa(`/sales/totals/${range}?region=${region}${comparisonQs(range, false, comp)}`))), fetchSales(wb(wsa(`/sales/trend/${range}?bucket=${bucket}&region=${region}${comparisonQs(range, false, comp)}`))), fetchSales(wb(wsa(`/sales/reps/${range}?region=${region}`))), fetchSales(wb(wsa(`/sales/customers/${range}?region=${region}&top=100`))), fetchSales(wb(wsa(`/sales/products/${range}?region=${region}&top=10`))).catch(() => []), fetchSales(wb(ws(`/sales/customers/${range}?top=200`))).catch(() => []),
      // Market row (all dealers in the market, covering reps) for "Dealers buying X of Y".
      fetchSales(wb(wsa(`/sales/territories/${range}`))).catch(() => [])]);
      if (this._alive) this.setState({
        data: {
          totals,
          trend,
          reps,
          customers,
          topProducts,
          acctSource,
          territories,
          bucket
        }
      });
    } catch (e) {
      if (this._alive) this.setState({
        error: true
      });
    }
  }
  periodPhrase() {
    const r = this.range();
    if (isCustomRange(r)) return "in the selected dates";
    if (r === "all_time") return "yet";
    return rangeLabelFor(r, RANGES).toLowerCase();
  }
  trendChartData(trend, bucket) {
    if (!trend || trend.length < 2) return null;
    const withPrev = hasPrevTrend(trend);
    const data = [withPrev ? ["Period", "Revenue", "Prev Revenue", "Orders", "Prev Orders"] : ["Period", "Revenue", "Orders"]];
    trend.forEach(t => {
      const rev = Math.round((Number(t.totalAmount) || 0) * 100) / 100;
      const second = Number(t.orders) || 0;
      if (withPrev) data.push([trendLabel(t.period, bucket), rev, Math.round((Number(t.prevTotalAmount) || 0) * 100) / 100, second, Number(t.prevOrders) || 0]);else data.push([trendLabel(t.period, bucket), rev, second]);
    });
    data.withPrev = withPrev;
    return data;
  }
  renderTopProducts(rows) {
    if (!rows || !rows.length) return null;
    return /*#__PURE__*/React.createElement(Card, {
      title: "Top products",
      hint: "by revenue in this market \xB7 click a product for its detail"
    }, /*#__PURE__*/React.createElement("div", {
      className: "tblwrap"
    }, /*#__PURE__*/React.createElement("table", {
      className: "territory-table wa-table"
    }, /*#__PURE__*/React.createElement("thead", null, /*#__PURE__*/React.createElement("tr", null, /*#__PURE__*/React.createElement("th", null, "Product"), /*#__PURE__*/React.createElement("th", {
      className: "n"
    }, "Orders"), /*#__PURE__*/React.createElement("th", {
      className: "n"
    }, "Units"), /*#__PURE__*/React.createElement("th", {
      className: "n"
    }, "Revenue"))), /*#__PURE__*/React.createElement("tbody", null, rows.map(p => /*#__PURE__*/React.createElement("tr", {
      key: p.productId,
      className: "clickable cust-row",
      onClick: () => this.props.history.push({
        pathname: `/Territory/${this.range()}/product/${encodeURIComponent(p.productId)}`,
        state: {
          title: p.title
        }
      }),
      title: `Open ${p.title || p.productId} product detail`
    }, /*#__PURE__*/React.createElement("td", null, /*#__PURE__*/React.createElement("span", {
      className: "name"
    }, titleCase(p.title) || p.productId), " ", /*#__PURE__*/React.createElement("span", {
      className: "code"
    }, p.productId)), /*#__PURE__*/React.createElement("td", {
      className: "n"
    }, num(p.orders)), /*#__PURE__*/React.createElement("td", {
      className: "n"
    }, num(p.quantity)), /*#__PURE__*/React.createElement("td", {
      className: "n"
    }, money(p.totalAmount))))))));
  }

  // The rep to name for a ZIP3 area: a non-house rep who owns ZIPs in it, else the
  // market's top covering rep by sales here, else nobody.
  areaRep(z, reps, covering) {
    const owns = (reps || []).filter(r => !isHouseRep(r)).find(r => {
      const list = Array.isArray(r.zip3s) ? r.zip3s : String(r.zip3Csv || "").split(",");
      return list.map(x => String(x).trim()).includes(String(z.zip3));
    });
    if (owns) return owns;
    if (z.uncovered) return null;
    const byRev = (covering || []).slice().sort((a, b) => (Number(b.totalAmount) || 0) - (Number(a.totalAmount) || 0));
    return byRev[0] || null;
  }

  // One specific sentence per area, built from that row's numbers.
  areaAction(z, rep) {
    const who = rep ? repName(rep) : null;
    const d = Number(z.dealers) || 0;
    if (d === 0) return who ? `No dealer here yet. Worth finding one for ${who}.` : "No dealer here yet. Worth finding one.";
    const lead = d === 1 ? z.booked > 0 ? "One dealer here, selling little." : `One dealer here, no orders ${this.periodPhrase()}.` : z.booked > 0 ? `${num(d)} dealers here sell little.` : `${num(d)} dealers here, no orders ${this.periodPhrase()}.`;
    return who ? `${lead} Ask ${who} to call them.` : `${lead} No rep covers this area yet, worth assigning one.`;
  }

  // ZIP3 areas in this state where website visits run ahead of orders. Always by delivery location.
  renderAreaGaps(region, reps, covering) {
    const cells = this.state.areaGaps;
    if (!cells || !region) return null;
    const st = String(region).toUpperCase();
    const rows = cells.filter(z => z.state === st && z.demand > 0 && z.gap > 10).sort((a, b) => b.gap - a.gap || b.demand - a.demand);
    if (!rows.length) return null;
    const LIMIT = 5;
    const all = this.state.gapsAll;
    const shown = all ? rows : rows.slice(0, LIMIT);
    return /*#__PURE__*/React.createElement(Card, {
      title: "Where people look but don't order",
      hint: "areas with lots of website visits and few orders \xB7 by delivery location, whatever the dropdown says",
      span: 12,
      cls: "tr-card"
    }, /*#__PURE__*/React.createElement("div", {
      className: "tblwrap"
    }, /*#__PURE__*/React.createElement("table", {
      className: "territory-table wa-table tr-table"
    }, /*#__PURE__*/React.createElement("thead", null, /*#__PURE__*/React.createElement("tr", null, /*#__PURE__*/React.createElement("th", {
      title: "Area by the first three digits of its ZIP codes, named after its largest place"
    }, "Area"), /*#__PURE__*/React.createElement("th", {
      title: "How far website visits run ahead of orders here, compared with every other area"
    }, "Gap"), /*#__PURE__*/React.createElement("th", {
      className: "n",
      title: "Website visits from this area in the period"
    }, "Website visits"), /*#__PURE__*/React.createElement("th", {
      className: "n",
      title: "Revenue booked at dealers delivering to this area in the period"
    }, "Sales"), /*#__PURE__*/React.createElement("th", {
      className: "n",
      title: "Dealer accounts with a delivery address in this area"
    }, "Dealers"), /*#__PURE__*/React.createElement("th", null, "What to do"))), /*#__PURE__*/React.createElement("tbody", null, shown.map(z => {
      const big = z.gap >= 35;
      return /*#__PURE__*/React.createElement("tr", {
        key: z.zip3
      }, /*#__PURE__*/React.createElement("td", null, /*#__PURE__*/React.createElement("span", {
        className: "name"
      }, titleCase(String(z.label || z.zip3).replace(/\s*\(\d{3}\)\s*$/, "").replace(/ area$/i, ""))), /*#__PURE__*/React.createElement("span", {
        className: "code"
      }, "ZIPs starting ", z.zip3)), /*#__PURE__*/React.createElement("td", null, /*#__PURE__*/React.createElement("span", {
        className: `tr-flag ${big ? "bad" : "warn"}`
      }, big ? "Big gap" : "Some gap")), /*#__PURE__*/React.createElement("td", {
        className: "n"
      }, num(z.demand)), /*#__PURE__*/React.createElement("td", {
        className: "n"
      }, z.booked ? money(z.booked) : /*#__PURE__*/React.createElement("span", {
        className: "dim"
      }, "none")), /*#__PURE__*/React.createElement("td", {
        className: "n"
      }, num(z.dealers)), /*#__PURE__*/React.createElement("td", {
        className: "tr-action"
      }, this.areaAction(z, this.areaRep(z, reps, covering))));
    })))), /*#__PURE__*/React.createElement(ShowAll, {
      total: rows.length,
      shown: LIMIT,
      open: all,
      onToggle: () => this.setState({
        gapsAll: !all
      })
    }));
  }
  render() {
    if (!hasTenantFeature("territoryRep")) {
      return /*#__PURE__*/React.createElement(CssContainer, {
        cls: "margin-squeeze"
      }, /*#__PURE__*/React.createElement("p", {
        className: "module-empty"
      }, "Territory & Rep Intelligence is not configured for this workspace."));
    }
    const {
      data,
      error,
      basis
    } = this.state;
    if (error) return /*#__PURE__*/React.createElement(Error, null);
    if (!data) return /*#__PURE__*/React.createElement(Loader, null);
    const region = this.region();
    const range = this.range();
    const {
      totals,
      trend,
      customers,
      bucket
    } = data;
    const reps = (data.reps || []).filter(r => !isHouseRep(r));
    const statesOf = r => String(r.states || "").split(",").map(s => s.trim().toUpperCase()).filter(Boolean);
    const covering = reps.filter(r => statesOf(r).includes(region.toUpperCase()));
    const coveringCodes = new Set(covering.map(r => r.repCode));
    const selling = reps.filter(r => Number(r.orders) > 0);
    const coveringSelling = covering.filter(r => Number(r.orders) > 0).length;
    const chartData = this.trendChartData(trend, bucket);
    const market = (data.territories || []).find(t => String(t.region || "").toUpperCase() === region.toUpperCase()) || null;
    const name = placeName(region);
    const avg = Number(totals.orders) > 0 ? (Number(totals.totalAmount) || 0) / Number(totals.orders) : 0;
    const coverText = covering.length === 0 ? "no rep covers it" : covering.length === 1 ? "1 rep covers it" : `${num(covering.length)} reps cover it`;
    const LIMIT = 10;
    const dealerRows = this.state.dealersAll ? customers : customers.slice(0, LIMIT);
    const notBuying = market ? Math.max(0, (Number(market.totalDealers) || 0) - (Number(market.activeDealers) || 0)) : 0;
    return /*#__PURE__*/React.createElement("div", {
      className: "territory-view territory-detail tr-view wa-view"
    }, /*#__PURE__*/React.createElement(PageHeader, {
      crumbs: /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(Link, {
        className: "tr-link",
        to: `/Territory/${range}`
      }, "Territory & Reps"), " \xB7 Market"),
      title: name,
      subtitle: `Dealers ${basis === "ship" ? "delivering to" : "with a head office in"} ${name} · ${coverText} · ${rangeLabelFor(range, RANGES)}`,
      actions: /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(StatusFilter, {
        range: range,
        value: this.state.status,
        onChange: v => this.setState({
          status: v,
          data: null,
          error: false
        }, () => this.load())
      }), /*#__PURE__*/React.createElement(BasisSelect, {
        value: basis,
        onChange: this.onBasis
      }), /*#__PURE__*/React.createElement(AccountFilterControl, {
        customers: this.state.data && this.state.data.acctSource || [],
        mode: this.state.acctMode,
        ids: this.state.acctIds,
        onApply: this.setAcctLens
      }))
    }), /*#__PURE__*/React.createElement(AccountFilterBanner, {
      customers: this.state.data && this.state.data.acctSource || [],
      mode: this.state.acctMode,
      ids: this.state.acctIds,
      onRemove: this.removeAcct,
      onClear: this.clearAcct
    }), covering.length === 0 && /*#__PURE__*/React.createElement("div", {
      className: "wa-notice warning"
    }, "No rep is assigned to ", name, ", so every sale here came from a rep in another territory or a house account."), /*#__PURE__*/React.createElement(Ribbon, {
      comparing: true,
      cols: 4,
      tiles: [{
        l: "Revenue",
        v: money(totals.totalAmount),
        d: kitDelta(deltaOfPaced(totals.totalAmount, totals.prevTotalAmount, range)),
        s: avg > 0 ? `${money(avg)} average order` : "",
        t: "Booked revenue from dealers in this market"
      }, {
        l: "Orders",
        v: num(totals.orders),
        d: kitDelta(deltaOfPaced(totals.orders, totals.prevOrders, range)),
        t: "Orders from dealers in this market"
      }, market && Number(market.totalDealers) > 0 ? {
        l: "Dealers buying",
        v: `${num(market.activeDealers)} of ${num(market.totalDealers)}`,
        s: notBuying > 0 ? `${num(notBuying)} ${notBuying === 1 ? "hasn't" : "haven't"} ordered ${this.periodPhrase()}` : "every dealer here ordered",
        t: "Dealers who ordered in the period, out of all dealers in the market",
        noDelta: true
      } : {
        l: "Dealers buying",
        v: num(totals.customers),
        t: "Dealers who ordered in the period"
      }, covering.length > 0 ? {
        l: "Reps",
        v: num(covering.length),
        s: coveringSelling === covering.length ? "all selling here" : `${num(coveringSelling)} selling here`,
        t: "Reps assigned to cover this market",
        noDelta: true
      } : {
        l: "Reps selling here",
        v: num(selling.length),
        s: "none assigned to cover it",
        t: "Reps who sold into this market in the period"
      }]
    }), /*#__PURE__*/React.createElement("div", {
      className: "wa-grid"
    }, chartData && /*#__PURE__*/React.createElement(Card, {
      title: "Sales trend",
      hint: "revenue bars \xB7 orders line \xB7 dashed = prior period when a comparison is set"
    }, /*#__PURE__*/React.createElement("div", {
      className: "territory-trend"
    }, /*#__PURE__*/React.createElement(Chart, {
      width: "100%",
      height: "320px",
      chartType: "ComboChart",
      loader: /*#__PURE__*/React.createElement(ChartLoader, null),
      data: chartData,
      options: {
        seriesType: "bars",
        series: chartData.withPrev ? {
          0: {
            targetAxisIndex: 0,
            color: "#ff6358" /* --plk-accent */,
            tooltipFormat: "currency"
          },
          1: {
            type: "line",
            targetAxisIndex: 0,
            color: "#aa46be" /* kendo palette purple */,
            lineDashStyle: [4, 3],
            tooltipFormat: "currency"
          },
          2: {
            type: "line",
            targetAxisIndex: 1,
            color: "#78d237",
            tooltipFormat: "{0:n0}"
          },
          3: {
            type: "line",
            targetAxisIndex: 1,
            color: "#ffd246",
            lineDashStyle: [4, 3],
            tooltipFormat: "{0:n0}"
          }
        } : {
          0: {
            targetAxisIndex: 0,
            color: "#ff6358" /* --plk-accent */,
            tooltipFormat: "currency"
          },
          1: {
            type: "line",
            targetAxisIndex: 1,
            color: "#78d237",
            tooltipFormat: "{0:n0}"
          }
        },
        vAxes: {
          0: {
            title: "Revenue",
            format: "short"
          },
          1: {
            title: "Orders",
            format: "0"
          }
        },
        legend: {
          position: "top"
        },
        backgroundColor: "transparent",
        chartArea: {
          left: 70,
          right: 60,
          top: 40,
          bottom: 40
        }
      }
    }))), /*#__PURE__*/React.createElement(SalesMapCard, {
      range: range,
      region: region,
      basis: basis,
      want: {
        coverage: true,
        dealers: true,
        heat: true
      },
      silentLoading: true,
      title: `Coverage, dealers and demand in ${name}`,
      hint: `hexes = rep coverage at ZIP grain · pins = dealers by ${basis === "ship" ? "delivery location" : "head office"} ZIP, sized by revenue, coloured by lapse band · heat = ZIP3 demand against booked revenue, always by delivery location`,
      onDealer: p => this.openCustomer(p.accountNumber || p.id)
    }), this.renderAreaGaps(region, data.reps, covering), /*#__PURE__*/React.createElement(Card, {
      title: `Reps in ${name}`,
      hint: "selling here this period, plus assigned reps with no sales here \xB7 click a rep for their page",
      span: 12
    }, /*#__PURE__*/React.createElement("div", {
      className: "tblwrap"
    }, /*#__PURE__*/React.createElement("table", {
      className: "territory-table wa-table tr-table"
    }, /*#__PURE__*/React.createElement("thead", null, /*#__PURE__*/React.createElement("tr", null, /*#__PURE__*/React.createElement("th", null, "Rep"), /*#__PURE__*/React.createElement("th", null, "Here"), /*#__PURE__*/React.createElement("th", null, "Also covers"), /*#__PURE__*/React.createElement("th", {
      className: "n"
    }, "Orders"), /*#__PURE__*/React.createElement("th", {
      className: "n",
      title: "Dealers in this market the rep sold to"
    }, "Dealers"), /*#__PURE__*/React.createElement("th", {
      className: "n"
    }, "Revenue"))), /*#__PURE__*/React.createElement("tbody", null, selling.concat(covering.filter(r => !(Number(r.orders) > 0))).map(r => {
      const others = statesOf(r).filter(s => s !== region.toUpperCase());
      const sells = Number(r.orders) > 0;
      return /*#__PURE__*/React.createElement("tr", {
        key: r.repCode,
        className: "clickable" + (sells ? "" : " dormant") + (r.status === "terminated" ? " terminated" : ""),
        onClick: () => this.openRep(r.repCode),
        title: `Open ${repName(r)}`
      }, /*#__PURE__*/React.createElement("td", null, /*#__PURE__*/React.createElement("span", {
        className: "name"
      }, repName(r)), /*#__PURE__*/React.createElement("span", {
        className: "code"
      }, "Rep ", r.repCode, r.status === "terminated" ? " · no longer active" : "")), /*#__PURE__*/React.createElement("td", null, coveringCodes.has(r.repCode) ? /*#__PURE__*/React.createElement(Pill, {
        tone: "success"
      }, "covers ", name) : /*#__PURE__*/React.createElement(Pill, {
        tone: "warning"
      }, "from another territory")), /*#__PURE__*/React.createElement("td", {
        title: placeList(others, 99)
      }, others.length ? placeList(others) : /*#__PURE__*/React.createElement("span", {
        className: "dim"
      }, "only ", name)), /*#__PURE__*/React.createElement("td", {
        className: "n"
      }, num(r.orders)), /*#__PURE__*/React.createElement("td", {
        className: "n"
      }, num(r.customers)), /*#__PURE__*/React.createElement("td", {
        className: "n strong"
      }, sells ? money(r.totalAmount) : /*#__PURE__*/React.createElement("span", {
        className: "dim"
      }, "none")));
    }), !selling.length && !covering.length ? /*#__PURE__*/React.createElement("tr", null, /*#__PURE__*/React.createElement("td", {
      colSpan: 6
    }, /*#__PURE__*/React.createElement(Empty, null, "No rep sales here in this period."))) : null)))), /*#__PURE__*/React.createElement(Card, {
      title: `Dealers in ${name}`,
      hint: `by revenue · placed by ${basis === "ship" ? "delivery location" : "head office"} · click a dealer for its page`,
      span: 12
    }, /*#__PURE__*/React.createElement("div", {
      className: "tblwrap"
    }, /*#__PURE__*/React.createElement("table", {
      className: "territory-table wa-table tr-table"
    }, /*#__PURE__*/React.createElement("thead", null, /*#__PURE__*/React.createElement("tr", null, /*#__PURE__*/React.createElement("th", null, "Dealer"), /*#__PURE__*/React.createElement("th", null, "Rep"), /*#__PURE__*/React.createElement("th", {
      className: "n"
    }, "Orders"), /*#__PURE__*/React.createElement("th", {
      className: "n"
    }, "Units"), /*#__PURE__*/React.createElement("th", {
      className: "n"
    }, "Revenue"))), /*#__PURE__*/React.createElement("tbody", null, dealerRows.map(c => {
      const rep = c.assignedRepName || c.assignedRepId;
      const house = isHouseRep({
        name: c.assignedRepName,
        repCode: c.assignedRepId
      });
      return /*#__PURE__*/React.createElement("tr", {
        key: c.accountNumber,
        className: "clickable",
        onClick: () => this.openCustomer(c.accountNumber),
        title: `Open ${titleCase(c.name || c.accountNumber)}`
      }, /*#__PURE__*/React.createElement("td", null, /*#__PURE__*/React.createElement("span", {
        className: "name"
      }, titleCase(c.name || c.accountNumber)), /*#__PURE__*/React.createElement("span", {
        className: "code"
      }, [c.city ? titleCase(c.city) : null, c.region].filter(Boolean).join(", ") || `Account ${c.accountNumber}`)), /*#__PURE__*/React.createElement("td", null, house ? "House account" : rep ? titleCase(rep) : /*#__PURE__*/React.createElement("span", {
        className: "dim"
      }, "No rep")), /*#__PURE__*/React.createElement("td", {
        className: "n"
      }, num(c.orders)), /*#__PURE__*/React.createElement("td", {
        className: "n"
      }, num(c.quantity)), /*#__PURE__*/React.createElement("td", {
        className: "n strong"
      }, money(c.totalAmount)));
    }), !customers.length ? /*#__PURE__*/React.createElement("tr", null, /*#__PURE__*/React.createElement("td", {
      colSpan: 5
    }, /*#__PURE__*/React.createElement(Empty, null, "No dealer orders here in this period."))) : null))), /*#__PURE__*/React.createElement(ShowAll, {
      total: customers.length,
      shown: LIMIT,
      open: this.state.dealersAll,
      onToggle: () => this.setState({
        dealersAll: !this.state.dealersAll
      })
    })), this.renderTopProducts(data.topProducts)));
  }
}
export default withRouter(TerritoryDetailViewComponent);